import { z } from 'zod';
import { supabase } from '@/shared/supabase/client';
import { identifiantDocument } from '@/shared/referentiel/chantier';
import type { DepotModele } from '../types';
import {
  COLONNES_MODELE,
  COLONNES_TYPE_DOCUMENT,
  ligneDeModele,
  ligneModele,
  ligneTypeDocument,
  messageRefus,
  versModele,
} from './lignes-chantiers';

const lignesId = z.array(z.object({ id: z.string() }));

/* Un libellé déjà pris, un document encore exigé : la base refuse. */
const DOUBLON = { '23505': 'Ce document existe déjà.' };

/**
 * La chaîne modèle et le catalogue des documents (db/009-chaine.sql). La
 * changer ne touche que les chantiers créés ensuite.
 */
export const modeleSupabase: DepotModele = {
  async lire() {
    const { data, error } = await supabase()
      .from('modele_etapes')
      .select(COLONNES_MODELE)
      .order('ordre');
    if (error) throw new Error(messageRefus(error, 'La chaîne d’étapes n’a pas pu être lue.'));
    return versModele(z.array(ligneModele).parse(data));
  },

  async ajouter(saisie) {
    /* À la fin, avant la réception du chantier : `ordonner` la remet derrière. */
    const actuel = await this.lire();
    const { error } = await supabase()
      .from('modele_etapes')
      .insert({ ...ligneDeModele(saisie), ordre: actuel.length + 1 });
    if (error) throw new Error(messageRefus(error, 'L’étape n’a pas pu être ajoutée.'));
    await this.ordonner(actuel.filter((m) => !m.finale).map((m) => m.id));
  },

  async modifier(id, saisie) {
    const { data, error } = await supabase()
      .from('modele_etapes')
      .update(ligneDeModele(saisie))
      .eq('id', id)
      .select('id');
    if (error) throw new Error(messageRefus(error, 'L’étape n’a pas pu être modifiée.'));
    if (lignesId.parse(data).length === 0) throw new Error('Étape introuvable.');
  },

  async supprimer(id) {
    const { data, error } = await supabase()
      .from('modele_etapes')
      .delete()
      .eq('id', id)
      .select('id');
    if (error) throw new Error(messageRefus(error, 'L’étape n’a pas pu être retirée.'));
    if (lignesId.parse(data).length === 0) throw new Error('Étape introuvable.');
  },

  async ordonner(ids) {
    /* Les étapes ajoutées entre-temps suivent : on complète la liste donnée. */
    const { data, error } = await supabase()
      .from('modele_etapes')
      .select('id, ordre, finale')
      .order('ordre');
    if (error) throw new Error(messageRefus(error, 'La chaîne n’a pas pu être rangée.'));
    const toutes = z
      .array(z.object({ id: z.string(), ordre: z.number(), finale: z.boolean() }))
      .parse(data)
      .filter((m) => !m.finale)
      .map((m) => m.id);
    const ordre = [
      ...ids.filter((id) => toutes.includes(id)),
      ...toutes.filter((id) => !ids.includes(id)),
    ];
    const rangement = await supabase().rpc('ordonner_modele', { p_ids: ordre });
    if (rangement.error) {
      throw new Error(messageRefus(rangement.error, 'La chaîne n’a pas pu être rangée.'));
    }
  },

  async documents() {
    const { data, error } = await supabase()
      .from('types_document')
      .select(COLONNES_TYPE_DOCUMENT)
      .order('libelle');
    if (error) throw new Error(messageRefus(error, 'Les documents n’ont pas pu être lus.'));
    return z.array(ligneTypeDocument).parse(data);
  },

  async ajouterDocument(libelle) {
    const nom = libelle.trim();
    if (nom === '') throw new Error('Donnez un nom au document.');
    const existants = await this.documents();
    if (existants.some((t) => t.libelle.toLowerCase() === nom.toLowerCase())) {
      throw new Error('Ce document existe déjà.');
    }
    const ligne = {
      id: identifiantDocument(
        nom,
        existants.map((t) => t.id)
      ),
      libelle: nom,
    };
    const { error } = await supabase().from('types_document').insert(ligne);
    if (error) throw new Error(messageRefus(error, 'Le document n’a pas pu être ajouté.', DOUBLON));
    return ligne;
  },

  async renommerDocument(id, libelle) {
    const nom = libelle.trim();
    if (nom === '') throw new Error('Donnez un nom au document.');
    const { data, error } = await supabase()
      .from('types_document')
      .update({ libelle: nom })
      .eq('id', id)
      .select('id');
    if (error) throw new Error(messageRefus(error, 'Le document n’a pas pu être renommé.'));
    if (lignesId.parse(data).length === 0) throw new Error('Document introuvable.');
  },

  async supprimerDocument(id) {
    const { data, error } = await supabase()
      .from('types_document')
      .delete()
      .eq('id', id)
      .select('id');
    if (error) throw new Error(messageRefus(error, 'Le document n’a pas pu être supprimé.'));
    if (lignesId.parse(data).length === 0) throw new Error('Document introuvable.');
  },
};
